import axios from "../../axios";
import toast from "../../hooks/toast";
import logger from "../../shared/Logger";
import { buildApiV3Url, buildApiV3UrlWithQuery } from "../../shared/Utils";
import { getPreferenceValue } from "../preferencesSlice/preferencesActions.ts";
import {
    setAccessibleBuckets,
    setAssignedUsers,
    setAvailableLocations,
    setBucketNoData,
    setBucketsDetail,
    setIsLoading,
    setIsUsersLoading,
    setLinkedLocations,
    setPage,
    setPageToNull,
    setUsers,
} from "./index";

const [showToast] = toast();

export const fetchBuckets = (top, skip, paginateDetails) => async (dispatch, getState) => {
    const { isLoading, sortColumn } = getState().buckets;
    const { rowsPerPage } = getState().session;
    top = top || rowsPerPage;
    if (isLoading) {
        return;
    }
    dispatch(setIsLoading(true));
    const sort = `${sortColumn[0]} ${sortColumn[1] ? "ASC" : "DESC"}`;
    const filter = "internal eq false";
    const apiUrl = buildApiV3UrlWithQuery("buckets", filter, "", top, skip, sort);

    try {
        const response = await axios.get(apiUrl, {
            withCredentials: true,
        });
        if (!response?.data) {
            dispatch(setPageToNull());
        } else {
            const bucketData = response.data.value;
            const totalCount = response.data.count;
            const paginateData = paginateDetails
                ? {
                      pageNumber: paginateDetails.currentPage,
                      startIndex: paginateDetails.startIndex,
                      endIndex: paginateDetails.endIndex,
                      rowsPerPage: paginateDetails.rowsPerPage,
                      totalRecords: totalCount,
                  }
                : {
                      pageNumber: 1,
                      startIndex: 0,
                      endIndex: 19,
                      rowsPerPage: 20,
                      totalRecords: totalCount,
                  };
            if (totalCount === 0) {
                dispatch(setBucketNoData("bucketGrid"));
            }
            dispatch(
                setPage({
                    data: bucketData,
                    paginate: paginateData,
                })
            );
            dispatch(setBucketsDetail(bucketData));
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.title || "Failed to read buckets";
        showToast("error", errorMessage);
        dispatch(setPageToNull());
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const fetchBucketsAssignedPreference = () => dispatch => {
    try {
        const parsedPref = dispatch(getPreferenceValue("buckets-assigned"));
        if (Array.isArray(parsedPref)) {
            return parsedPref;
        }
    } catch {
        logger(2, "error", {
            message: "Error fetching buckets assigned preference:",
        });
    }
    return [];
};

export const fetchAccessibleBuckets = () => async (dispatch, getState) => {
    let { bucketsDetail } = getState().buckets;

    if (bucketsDetail.length === 0) {
        await dispatch(fetchBuckets());
        bucketsDetail = getState().buckets.bucketsDetail;
    }

    const accessibleBucketsId = await dispatch(fetchBucketsAssignedPreference());
    const bucketsAccessible = bucketsDetail.filter(bucket =>
        accessibleBucketsId.includes(bucket.id)
    );
    dispatch(setAccessibleBuckets(bucketsAccessible));
};

export const createBucket = payload => async dispatch => {
    dispatch(setIsLoading(true));
    let successResponse = false;

    try {
        const response = await axios.post(buildApiV3Url("buckets"), payload, {
            withCredentials: true,
        });

        if (response.status === 200) {
            successResponse = true;
            dispatch(setIsLoading(false));
            showToast("success", "You've created a bucket.");
            dispatch(fetchBuckets());
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.title || "Failed to create bucket.";
        showToast("error", errorMessage);
    } finally {
        // To avoid loading state reset twice in case of success
        if (!successResponse) {
            dispatch(setIsLoading(false));
        }
    }
};

export const updateBucket = (bucketId, payload) => async dispatch => {
    dispatch(setIsLoading(true));
    let successResponse = false;

    try {
        const response = await axios.put(buildApiV3Url(`buckets/${bucketId}`), payload, {
            withCredentials: true,
        });

        if (response.status === 200) {
            successResponse = true;
            dispatch(setIsLoading(false));
            showToast("success", "The bucket is updated.");
            dispatch(fetchBuckets());
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.title || "Failed to update bucket.";
        showToast("error", errorMessage);
    } finally {
        if (!successResponse) {
            dispatch(setIsLoading(false));
        }
    }
};

export const deleteBucket = bucketId => async dispatch => {
    dispatch(setIsLoading(true));
    let successResponse = false;

    try {
        const response = await axios.delete(buildApiV3Url(`buckets/${bucketId}`), {
            withCredentials: true,
        });

        if (response.status === 200) {
            successResponse = true;
            dispatch(setIsLoading(false));
            showToast("success", "The bucket is deleted.");
            dispatch(fetchBuckets());
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.title || "Failed to delete bucket.";
        showToast("error", errorMessage);
    } finally {
        if (!successResponse) {
            dispatch(setIsLoading(false));
        }
    }
};

const mapLocations = locations => {
    return locations.map(location => {
        return {
            id: location.id,
            name: location.name,
            bucketId: location.bucketId,
        };
    });
};

export const fetchBucketLocations = bucketId => async (dispatch, getState) => {
    const { isLoading } = getState().buckets;
    const {
        settingData: { id: clientId },
    } = getState().setting;
    if (isLoading) {
        return;
    }
    dispatch(setIsLoading(true));
    try {
        const apiCalls = [];

        const availableLocationsFilter = `bucketId ne ${bucketId} and clientId eq ${clientId}`;
        const availableLocationsUrl = buildApiV3UrlWithQuery(
            "retail-locations",
            availableLocationsFilter,
            "",
            1000,
            0,
            ""
        );
        const availableLocationsCall = axios.get(availableLocationsUrl, {
            withCredentials: true,
        });
        apiCalls.push(availableLocationsCall);

        if (bucketId) {
            const linkedLocationsFilter = `bucketId eq ${bucketId}`;
            const linkedLocationsUrl = buildApiV3UrlWithQuery(
                "retail-locations",
                linkedLocationsFilter,
                "",
                1000,
                0,
                ""
            );
            const linkedLocationsCall = axios.get(linkedLocationsUrl, {
                withCredentials: true,
            });
            apiCalls.push(linkedLocationsCall);
        }

        // Fail fast strategy
        const results = await Promise.all(apiCalls);

        const availableLocationsResponse = results[0];
        if (availableLocationsResponse?.data) {
            const locations = availableLocationsResponse.data.value;
            const locationsMap = mapLocations(locations);
            dispatch(setAvailableLocations(locationsMap));
        } else {
            dispatch(setAvailableLocations([]));
        }

        if (bucketId && results[1]) {
            const linkedLocationsResponse = results[1];
            if (linkedLocationsResponse?.data) {
                const locations = linkedLocationsResponse.data.value;
                const locationsMap = mapLocations(locations);
                dispatch(setLinkedLocations(locationsMap));
                if (locationsMap.length === 0) {
                    dispatch(setBucketNoData("bucketLocations"));
                }
            } else {
                dispatch(setLinkedLocations([]));
            }
        } else {
            dispatch(setLinkedLocations([]));
            showToast("error", "Error fetching assigned locations.");
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.title || "Failed to fetch Bucket Locations.";
        showToast("error", errorMessage);
        dispatch(setAvailableLocations([]));
        dispatch(setLinkedLocations([]));
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const linkBucketToLocation = payload => async dispatch => {
    dispatch(setIsLoading(true));
    let successResponse = false;

    try {
        const response = await axios.post(buildApiV3Url("locations/link-to-bucket"), payload, {
            withCredentials: true,
        });

        if (response.status === 200) {
            successResponse = true;
            dispatch(setIsLoading(false));
            if (payload.LocationIds.length > 1) {
                showToast("success", "Selected locations are added to the bucket.");
            } else {
                showToast("success", "The location is added to the bucket.");
            }
            dispatch(fetchBucketLocations(payload.BucketId));
        }
    } catch (error) {
        const errorMessage =
            error?.response?.data?.title || "Failed to link bucket to selected locations.";
        showToast("error", errorMessage);
    } finally {
        if (!successResponse) {
            dispatch(setIsLoading(false));
        }
    }
};

export const unlinkBucketFromLocation = (payload, bucketId) => async dispatch => {
    dispatch(setIsLoading(true));
    let successResponse = false;

    try {
        const response = await axios.post(buildApiV3Url(`locations/unlink-from-bucket`), payload, {
            withCredentials: true,
        });

        if (response.status === 200) {
            successResponse = true;
            dispatch(setIsLoading(false));
            if (payload.LocationIds.length > 1) {
                showToast("success", "Selected locations are removed from the bucket.");
            } else {
                showToast("success", "The location is removed from the bucket.");
            }
            dispatch(fetchBucketLocations(bucketId));
        }
    } catch (error) {
        const errorMessage =
            error?.response?.data?.title || "Failed to unlink bucket from selected locations.";
        showToast("error", errorMessage);
    } finally {
        if (!successResponse) {
            dispatch(setIsLoading(false));
        }
    }
};

const updateUsersState = (payload, isAssigning) => async (dispatch, getState) => {
    const { users, assignedUsers } = getState().buckets;
    const userIds = payload.UserIds;

    if (isAssigning) {
        // When assigning: move users from available to assigned
        const usersToAssign = users.filter(user => userIds.includes(user.id));
        const newAssignedUsers = [...assignedUsers, ...usersToAssign];
        dispatch(setAssignedUsers([...newAssignedUsers]));
    } else {
        // When unassigning: remove users from assigned list
        const newAssignedUsers = assignedUsers.filter(user => !userIds.includes(user.id));
        dispatch(setAssignedUsers([...newAssignedUsers]));
    }
};

export const fetchAllBucketUsers = () => async (dispatch, getState) => {
    const { isLoading } = getState().buckets;
    if (isLoading) {
        return;
    }
    dispatch(setIsLoading(true));
    const apiUrl = buildApiV3Url("buckets/all-users");
    try {
        const response = await axios.get(apiUrl, {
            withCredentials: true,
        });
        if (response.status === 200) {
            if (response?.data) {
                const users = response.data;
                dispatch(setUsers(users));
            } else {
                dispatch(setUsers([]));
            }
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.title || "Failed to fetch users.";
        showToast("error", errorMessage);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const fetchBucketUsersByBucketId = bucketId => async (dispatch, getState) => {
    const { isUsersLoading } = getState().buckets;
    if (isUsersLoading) {
        return;
    }
    dispatch(setIsUsersLoading(true));
    const apiUrl = buildApiV3Url(`buckets/${bucketId}/users`);
    try {
        const response = await axios.get(apiUrl, {
            withCredentials: true,
        });
        if (response.status === 200) {
            if (response?.data?.returnValue) {
                const assignedUsers = response.data.returnValue;
                dispatch(setAssignedUsers(assignedUsers));
                if (assignedUsers.length === 0) {
                    dispatch(setBucketNoData("bucketUsers"));
                }
            } else {
                dispatch(setAssignedUsers([]));
            }
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.title || "Failed to fetch bucket users.";
        showToast("error", errorMessage);
    } finally {
        dispatch(setIsUsersLoading(false));
    }
};

export const assignBucketToUser = (payload, bucketId) => async (dispatch, getState) => {
    const { isLoading } = getState().buckets;
    if (isLoading) {
        return;
    }
    dispatch(setIsLoading(true));
    try {
        const response = await axios.post(buildApiV3Url(`buckets/${bucketId}/users`), payload, {
            withCredentials: true,
        });
        if (response.status === 200) {
            dispatch(updateUsersState(payload, true));
            if (payload.UserIds.length > 1) {
                showToast("success", "Selected users are assigned to the bucket.");
            } else {
                showToast("success", "The user is assigned to the bucket.");
            }
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.title || "Failed to assign bucket to user.";
        showToast("error", errorMessage);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const unAssignBucketFromUser = (payload, bucketId) => async (dispatch, getState) => {
    const { isLoading } = getState().buckets;
    if (isLoading) {
        return;
    }
    dispatch(setIsLoading(true));
    try {
        const response = await axios.delete(buildApiV3Url(`buckets/${bucketId}/users`), {
            data: payload,
            withCredentials: true,
        });
        if (response.status === 200) {
            dispatch(updateUsersState(payload, false));
            if (payload.UserIds.length > 1) {
                showToast("success", "Selected users are removed from the bucket.");
            } else {
                showToast("success", "The user is removed from the bucket.");
            }
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.title || "Failed to remove bucket from user.";
        showToast("error", errorMessage);
    } finally {
        dispatch(setIsLoading(false));
    }
};
